import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
	HubConnectionState
} from "@aspnet/signalr";
import { BehaviorSubject } from "rxjs";
import { SignalRHttpClient } from "./signalr-http.service";
import { HttpClient } from "@angular/common/http";
import { UrlFactory } from "src/app/core/httpclient/interceptor/url-factory";
import { CoreToolbox } from "../utils/core-toolbox";
import { AppConstants } from '../utils/app.const';

const WAIT_UNTIL_ASPNETCORE_IS_READY_DELAY_IN_MS = 100;

/**
 * Common handle for SignalRService
 */
export abstract class SignalRService {
	hubConnection: HubConnection;
	connectionEstablished$ = new BehaviorSubject<boolean>(false);

	private timeoutReconnect = null;

	constructor(protected http: HttpClient) { }

	/**
	 * Create connection with optional.
	 */
	public createConnection(
		url: string,
		opts?: {
			withAutomaticReconnect?: boolean;
			withAuthenticate?: boolean;
			accessToken?: () => string;
		}
	) {
		this.hubConnection = new HubConnectionBuilder()
			.withUrl(UrlFactory.generateUrl() + url, {
				accessTokenFactory: opts ? opts.accessToken : () => null,
				httpClient: this.getHttpClient()
			})
			.configureLogging(LogLevel.Information)
			.build();

		if (opts && opts.withAutomaticReconnect) {
			this.automaticReconnect(this.hubConnection);
		}
	}

	/**
	 * Start connection with hub option.
	 */
	public startConnection() {
		if (this.hubConnection.state === HubConnectionState.Connected) {
			return;
		}

		setTimeout(() => {
			this.hubConnection
				.start()
				.then(() => {
					this.connectionEstablished$.next(true);
					CoreToolbox.networkService.pushState({ connected: true, connectionType: null });
				})
				.catch(err => {
					console.error("Error while establishing connection :(");
					CoreToolbox.networkService.pushState({ connected: false, connectionType: null });
					this.startInterval(this.hubConnection);
				});
		}, WAIT_UNTIL_ASPNETCORE_IS_READY_DELAY_IN_MS);
	}

	/**
	 * Stop connection. Skip auto reconnect.
	 */
	public stopConnection(): void {
		// Clear timeout reconnection
		if (this.timeoutReconnect) { clearTimeout(this.timeoutReconnect); }
		if (this.isConnected()) {
			this.hubConnection.stop();
		}
	}

	public isConnected(): boolean {
		return this.hubConnection.state === HubConnectionState.Connected;
	}

	/**
	 * Get signalr http client.
	 */
	private getHttpClient() {
		return new SignalRHttpClient(this.http);
	}

	/**
	 * Handle automatic reconnect
	 */
	private automaticReconnect(hub: HubConnection) {
		hub.onclose(error => {
			console.error("Connection was stopped with error", error);
			this.startInterval(hub);
		});
	}

	/**
	 * Start connection interval
	 */
	protected startInterval(hub: HubConnection, attemp = 0) {
		this.timeoutReconnect = setTimeout(() => {
			hub.start()
				.then(success => {
					/** Start OK */
					this.connectionEstablished$.next(true);
					CoreToolbox.networkService.pushState({ connected: true, connectionType: null });
				})
				.catch(error => {
					console.error("Try connect failed: ", error);
					CoreToolbox.networkService.pushState({ connected: false, connectionType: null });
					if (attemp < AppConstants.TRY_CONNECTE_ATTEMP) {
						this.startInterval(hub, !attemp ? 1 : ++attemp);
					} else {
						console.error("Exceeded try attempt reconnection :(.");
					}
				});
		}, 2000);
	}
}
